import FiltersRoot from "@/components/shared/data-table/filters";
import { GlobalFilterInput } from "@/components/shared/data-table/filters/inputs/global-filter";
import { SingleSelectInput } from "@/components/shared/filter-inputs/single-select-filter-input";
import { DatePickerWithRangeInput } from "@/components/shared/form/data-picker-with-range-input";
import { MultiSelectInput } from "@/components/shared/form/multi-select-input";
import { NumberRangeInput } from "@/components/shared/form/number-range-input";
import { useGetAllBankAccounts } from "@/queries/bank-accounts/get-all";
import { CircleMinus, CirclePlus, EuroIcon, Landmark } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export function TransactionsTableFilters({
  globalSearch,
  setGlobalSearch,
  filters,
  setFilters,
}: {
  globalSearch: string;
  setGlobalSearch: (value: string) => void;
  filters: any;
  setFilters: any;
}) {
  const { bankAccounts } = useGetAllBankAccounts();

  const { t } = useTranslation();

  function handleSubmit(newFilters: any) {
    setFilters(newFilters);
  }

  const bankAccountOptions = useMemo(
    () =>
      bankAccounts
        .filter(
          (bankAccount) =>
            !bankAccount.bankConnection ||
            bankAccount.bankConnection.txnImportEnabled,
        )
        .map((bankAccount) => ({
          value: bankAccount.bankAccountId,
          label:
            bankAccount.name ||
            bankAccount.iban ||
            t("transactions.filters.bank_account.unknown"),
          description: bankAccount.name ? bankAccount.iban : undefined,
          icon:
            bankAccount.bankConnection &&
            (() => (
              <BankConnectionIcon
                url={bankAccount.bankConnection!.metadata.bank.icon?.url}
              />
            )),
        })),
    [bankAccounts, t],
  );

  const sideOptions = useMemo(
    () => [
      {
        value: "credit",
        label: t("transactions.filters.side.options.credit"),
        icon: CirclePlus,
      },
      {
        value: "debit",
        label: t("transactions.filters.side.options.debit"),
        icon: CircleMinus,
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-row gap-2 flex-wrap">
      <div className="relative flex-1 min-w-[200px] max-w-[400px]">
        <GlobalFilterInput
          placeholder={t(
            "transactions.todos.filters.global_search.placeholder",
          )}
          value={globalSearch}
          onChange={setGlobalSearch}
        />
      </div>
      <FiltersRoot onSubmit={handleSubmit} values={filters}>
        <DatePickerWithRangeInput
          name="bookingDate"
          placeholder={t("transactions.filters.created_at.placeholder")}
        />
        <MultiSelectInput
          name="bankAccountId"
          options={bankAccountOptions}
          placeholder={t("transactions.filters.bank_account_id.placeholder")}
          formItemClassName="w-fit"
          icon={Landmark}
        />
        <NumberRangeInput
          name="amount"
          icon={EuroIcon}
          placeholder={t("transactions.filters.amount.placeholder")}
          formItemClassName="w-fit"
          initialMode="range"
          singleTabTitle={t("range_input.amount.single_tab")}
          rangeTabTitle={t("range_input.amount.range_tab")}
          singleLabel={t("range_input.amount.label")}
          minLabel={t("range_input.amount.min_label")}
          maxLabel={t("range_input.amount.max_label")}
          singlePlaceholder={t("range_input.amount.single_placeholder")}
          minPlaceholder={t("range_input.amount.min_placeholder")}
          maxPlaceholder={t("range_input.amount.max_placeholder")}
        />
        <SingleSelectInput
          name="side"
          options={sideOptions}
          placeholder={t("transactions.filters.side.placeholder")}
          formItemClassName="w-fit"
          className="min-w-[140px]"
          placeholderIcon={
            <div className="flex items-center h-4 w-4 justify-center">
              <span className="text-sm font-medium">±</span>
            </div>
          }
        />
      </FiltersRoot>
    </div>
  );
}

function BankConnectionIcon({ url }: { url?: string | null }) {
  if (!url) {
    return (
      <div className="my-0.5 mr-1 h-5 w-5 rounded bg-muted flex items-center justify-center">
        <Landmark className="h-3 w-3 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt="bank-connection-icon"
      width={20}
      height={20}
      className="my-0.5 object-contain mr-1"
    />
  );
}
